// src/pages/ccp/BakingCcpConfigPage.js

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import bakingCcpService from '../../services/bakingCcpService';
import { useLanguage, t } from '../../context/LanguageContext';
import { getCcpFlowLabels } from '../../config/ccpFlowLabels';

export default function BakingCcpConfigPage() {
  const { lang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cfg, setCfg] = useState(null);

  // Keep error for "load" failures
  const [error, setError] = useState('');

  // NEW: save feedback (success + error)
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' }); // type: 'success' | 'danger' | ''
  const L = getCcpFlowLabels(cfg?.flowType || 'baking');

  const [form, setForm] = useState({
    maxMinutesBetweenTemps: 60,
    maxMinutesToFreezer: 360,
    ovenStartMinF: '',
    ovenStartMaxF: '',
    packagingQuestionsJson: [],
    flowType: 'baking',
    pasteurizationHoldMinutes: '',
    minAgingHours: '',
    maxAgingHours: '',
  });

  const fetchConfig = async () => {
    setLoading(true);
    setError('');
    setSaveStatus({ type: '', message: '' }); // clear banners on refresh

    try {
      const data = await bakingCcpService.getConfig();
      const c = data?.config;

      setCfg(c || null);
      setForm({
        maxMinutesBetweenTemps: c?.maxMinutesBetweenTemps ?? 60,
        maxMinutesToFreezer: c?.maxMinutesToFreezer ?? 360,
        ovenStartMinF: c?.ovenStartMinF ?? '',
        ovenStartMaxF: c?.ovenStartMaxF ?? '',
        packagingQuestionsJson: Array.isArray(c?.packagingQuestionsJson) ? c.packagingQuestionsJson : [],
        flowType: c?.flowType || 'baking',
        pasteurizationHoldMinutes: c?.pasteurizationHoldMinutes ?? '',
        minAgingHours: c?.minAgingHours ?? '',
        maxAgingHours: c?.maxAgingHours ?? '',
      });
    } catch (e) {
      console.error(e);
      setError(t('Failed to load Baking CCP configuration.', 'Error al cargar la configuración del CCP de Horneado.', lang));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addQuestion = () => {
    setForm((f) => ({ ...f, packagingQuestionsJson: [...f.packagingQuestionsJson, ''] }));
  };

  const updateQuestion = (idx, value) => {
    setForm((f) => {
      const next = [...f.packagingQuestionsJson];
      next[idx] = value;
      return { ...f, packagingQuestionsJson: next };
    });
  };

  const removeQuestion = (idx) => {
    setForm((f) => {
      const next = [...f.packagingQuestionsJson];
      next.splice(idx, 1);
      return { ...f, packagingQuestionsJson: next };
    });
  };

  const canSave = useMemo(() => {
    const a = Number(form.maxMinutesBetweenTemps);
    const b = Number(form.maxMinutesToFreezer);
    if (!Number.isFinite(a) || a <= 0) return false;
    if (!Number.isFinite(b) || b <= 0) return false;
    return true;
  }, [form.maxMinutesBetweenTemps, form.maxMinutesToFreezer]);

  // Optional: auto-hide success after 3 seconds
  useEffect(() => {
    if (saveStatus.type !== 'success') return;
    const timer = setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaveStatus({ type: '', message: '' });

    try {
      const payload = {
        maxMinutesBetweenTemps: Number(form.maxMinutesBetweenTemps),
        maxMinutesToFreezer: Number(form.maxMinutesToFreezer),
        ovenStartMinF: form.ovenStartMinF === '' ? null : Number(form.ovenStartMinF),
        ovenStartMaxF: form.ovenStartMaxF === '' ? null : Number(form.ovenStartMaxF),
        packagingQuestionsJson: (form.packagingQuestionsJson || []).map((q) => String(q || '').trim()).filter(Boolean),
        flowType: form.flowType,
        pasteurizationHoldMinutes: form.pasteurizationHoldMinutes === '' ? null : Number(form.pasteurizationHoldMinutes),
        minAgingHours: form.minAgingHours === '' ? null : Number(form.minAgingHours),
        maxAgingHours: form.maxAgingHours === '' ? null : Number(form.maxAgingHours),
      };

      const res = await bakingCcpService.saveConfig(payload);

      // Some APIs return { config }, others return the config directly—handle both.
      const nextCfg = res?.config ?? res ?? null;

      setCfg(nextCfg);
      setSaveStatus({ type: 'success', message: t('Configuration saved successfully.', 'Configuración guardada exitosamente.', lang) });
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t('Failed to save Baking CCP configuration.', 'Error al guardar la configuración del CCP de Horneado.', lang);
      setSaveStatus({ type: 'danger', message: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h4 className="mb-0">{t(`${L.stage1Action.en} CCP Configuration`, `Configuración de CCP de ${L.stage1Action.es}`, lang)}</h4>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {t('Company-scoped settings for interval alerts, freezer window, and packaging checklist.', 'Configuración de la empresa para alertas de intervalos, ventana al congelador y lista de empaque.', lang)}
            </div>
          </div>

          <div className="d-flex gap-2">
            <Link className="btn btn-outline-secondary" to="/ccp/baking/start">
              {t('Back', 'Volver', lang)}
            </Link>
            <button className="btn btn-outline-primary" onClick={fetchConfig} disabled={loading || saving}>
              {loading ? t('Loading…', 'Cargando…', lang) : t('Refresh', 'Actualizar', lang)}
            </button>
            <button className="btn btn-primary" onClick={handleSave} disabled={!canSave || saving || loading}>
              {saving ? t('Saving…', 'Guardando…', lang) : t('Save', 'Guardar', lang)}
            </button>
          </div>
        </div>

        {/* NEW: Save feedback banner */}
        {saveStatus.message && (
          <div className={`alert alert-${saveStatus.type} mt-3 mb-0`} role="alert">
            {saveStatus.message}
          </div>
        )}

        {/* Existing: Load error banner */}
        {error && (
          <div className="alert alert-danger mt-3 mb-0" role="alert">
            {error}
          </div>
        )}

        <div className="row mt-3 g-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h6 className="mb-2">{t('Production Flow', 'Flujo de Producción', lang)}</h6>
                <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                  {t('Controls the terminology and stages shown throughout this module for your company.', 'Controla la terminología y las etapas mostradas en este módulo para su empresa.', lang)}
                </div>
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn ${form.flowType === 'baking' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setForm(f => ({ ...f, flowType: 'baking' }))}
                    disabled={saving || loading}
                  >
                    {t('Baking (oven → cart → blast)', 'Horneado (horno → carrito → congelador)', lang)}
                  </button>
                  <button
                    type="button"
                    className={`btn ${form.flowType === 'pasteurization' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setForm(f => ({ ...f, flowType: 'pasteurization' }))}
                    disabled={saving || loading}
                  >
                    {t('Pasteurization (pasteurize → age → churn)', 'Pasteurización (pasteurizar → añejar → batir)', lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card">
              <div className="card-body">
                <h6 className="mb-3">{t('Timing Rules', 'Reglas de Tiempo', lang)}</h6>

                <label className="form-label mb-1">{t('Max minutes between temperature readings', 'Minutos máximos entre lecturas de temperatura', lang)}</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={form.maxMinutesBetweenTemps}
                  onChange={(e) => setForm((f) => ({ ...f, maxMinutesBetweenTemps: e.target.value }))}
                />

                <label className="form-label mb-1 mt-3">{t(`Max minutes from ${L.vessel.en.toLowerCase()} to blast freezer`, `Minutos máximos de ${L.vessel.es.toLowerCase()} al congelador rápido`, lang)}</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={form.maxMinutesToFreezer}
                  onChange={(e) => setForm((f) => ({ ...f, maxMinutesToFreezer: e.target.value }))}
                />

                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  {t('Defaults: 60 minutes temp interval, 360 minutes (6 hours) to freezer.', 'Valores por defecto: 60 min entre temperaturas, 360 min (6 horas) al congelador.', lang)}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card">
              <div className="card-body">
                <h6 className="mb-3">{t(`${L.vessel.en} Start Expected Range (Optional)`, `Rango Esperado de Inicio de ${L.vessel.es} (Opcional)`, lang)}</h6>

                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label mb-1">{t('Min °F', 'Mín °F', lang)}</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.ovenStartMinF}
                      onChange={(e) => setForm((f) => ({ ...f, ovenStartMinF: e.target.value }))}
                      placeholder={L.vesselTempRangeMinPlaceholder}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label mb-1">{t('Max °F', 'Máx °F', lang)}</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.ovenStartMaxF}
                      onChange={(e) => setForm((f) => ({ ...f, ovenStartMaxF: e.target.value }))}
                      placeholder={L.vesselTempRangeMaxPlaceholder}
                    />
                  </div>
                </div>

                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  {t(`Used to flag an out-of-range ${L.vessel.en.toLowerCase()} start temperature during Start Production.`, `Se usa para marcar una temperatura de inicio de ${L.vessel.es.toLowerCase()} fuera de rango durante Iniciar Producción.`, lang)}
                </div>
              </div>
            </div>
          </div>

          {form.flowType === 'pasteurization' && (
            <div className="col-12">
              <div className="card border-warning">
                <div className="card-body">
                  <h6 className="mb-1">{t('Pasteurization Critical Limits', 'Límites Críticos de Pasteurización', lang)}</h6>
                  <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                    {t(
                      'The actual CCP is temperature held for a minimum duration (e.g. 155°F for 30 min), not just a single reading. Confirm the exact required time/temperature against your validated HACCP plan — these are defaults for flagging, not a substitute for it.',
                      'El CCP real es la temperatura sostenida por una duración mínima (ej. 155°F por 30 min), no solo una lectura puntual. Confirme el tiempo/temperatura exactos requeridos en su plan HACCP validado — esto solo sirve para marcar alertas, no lo reemplaza.',
                      lang
                    )}
                  </div>

                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label mb-1">{t('Required hold time (minutes)', 'Tiempo de sostenimiento requerido (minutos)', lang)}</label>
                      <input
                        type="number"
                        className="form-control"
                        min="1"
                        value={form.pasteurizationHoldMinutes}
                        onChange={(e) => setForm((f) => ({ ...f, pasteurizationHoldMinutes: e.target.value }))}
                        placeholder="e.g. 30"
                      />
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        {t('Live page flags Stop Pasteurizing if clicked before this elapses.', 'La página en vivo marca Detener Pasteurización si se hace antes de este tiempo.', lang)}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label mb-1">{t('Min aging hours (optional)', 'Horas mínimas de añejamiento (opcional)', lang)}</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        step="0.5"
                        value={form.minAgingHours}
                        onChange={(e) => setForm((f) => ({ ...f, minAgingHours: e.target.value }))}
                        placeholder="e.g. 4"
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label mb-1">{t('Max aging hours (optional)', 'Horas máximas de añejamiento (opcional)', lang)}</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        step="0.5"
                        value={form.maxAgingHours}
                        onChange={(e) => setForm((f) => ({ ...f, maxAgingHours: e.target.value }))}
                        placeholder="e.g. 24"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">{t('Packaging Checklist Questions', 'Preguntas de Lista de Empaque', lang)}</h6>
                  <button className="btn btn-outline-secondary btn-sm" onClick={addQuestion} disabled={saving || loading}>
                    + {t('Add question', 'Agregar pregunta', lang)}
                  </button>
                </div>

                <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                  {t('These appear at Complete Production and are stored with the run record.', 'Aparecen en Completar Producción y se guardan con el registro de la corrida.', lang)}
                </div>

                <div className="mt-3 d-flex flex-column gap-2">
                  {(form.packagingQuestionsJson || []).map((q, idx) => (
                    <div key={idx} className="d-flex gap-2">
                      <input
                        className="form-control"
                        value={q}
                        onChange={(e) => updateQuestion(idx, e.target.value)}
                        placeholder={`${t('Question', 'Pregunta', lang)} ${idx + 1}`}
                        disabled={saving || loading}
                      />
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => removeQuestion(idx)}
                        title={t('Remove', 'Eliminar', lang)}
                        disabled={saving || loading}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {(form.packagingQuestionsJson || []).length === 0 && (
                    <div className="text-muted">{t('No questions configured.', 'No hay preguntas configuradas.', lang)}</div>
                  )}
                </div>

                {cfg?.updatedAt && (
                  <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                    {t('Last updated:', 'Última actualización:', lang)} {new Date(cfg.updatedAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
